
import { useContext, useEffect, useState } from "react";
import { DollarSign, ShoppingCart, Users } from "lucide-react";

import MetricCard from "../../components/MetricCard";
import FinancialSummary from "../../components/FinancialSummary";
import BillingByPeriod from "../../components/BillingByPeriod";
import { ServicesbyType } from "../../components/ServicesbyType";

import { ServicesContext } from "../../context/ContextServices/context";
import { loadServices } from "../../context/ContextServices/actions";

import * as Styled from "./styled";

const Home = () => {
  const { state, dispatch } = useContext(ServicesContext);
  const [selectedMonth, setSelectedMonth] = useState("");

  useEffect(() => {
    loadServices(dispatch);
  }, [dispatch]);

  const services = state.services || [];

  const getServiceDate = (dataEntrada: any) => {
    if (!dataEntrada) {
      return new Date();
    }

    if (dataEntrada?.toDate) {
      return dataEntrada.toDate();
    }

    if (
      typeof dataEntrada === "string" &&
      dataEntrada.includes("-")
    ) {
      const [year, month, day] = dataEntrada.split("-");

      return new Date(
        Number(year),
        Number(month) - 1,
        Number(day)
      );
    }

    return new Date(dataEntrada);
  };

  const parseMoney = (value: any) => {
    if (value === undefined || value === null || value === "") {
      return 0;
    }

    return (
      parseFloat(
        String(value)
          .replace(/\./g, "")
          .replace(",", ".")
      ) || 0
    );
  };

  // 🔥 Formata dinheiro
  const formatMoney = (value: number) => {
    return value.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // 🔥 Retorna "Maio 2026"
  const getMonthYear = (serv: any) => {
    const d = getServiceDate(serv.dataEntrada);

    const month = d.toLocaleString("pt-BR", {
      month: "long",
    });

    const formattedMonth =
      month.charAt(0).toUpperCase() + month.slice(1);

    return `${formattedMonth} ${d.getFullYear()}`;
  };

  // 🔥 Filtra por mês selecionado
  const servicesOfMonth = selectedMonth
    ? services.filter(
        (serv: any) => getMonthYear(serv) === selectedMonth
      )
    : services;

  // ==========================
  // MÉTRICAS
  // ==========================

  const totalBilling = servicesOfMonth.reduce(
    (acc: number, serv: any) =>
      acc + parseMoney(serv.valorConserto),
    0
  );

  const totalServices = servicesOfMonth.length;

  const totalClients = new Set(
    servicesOfMonth.map((serv: any) => serv.placa)
  ).size;

  const totalExpenses = servicesOfMonth.reduce(
    (acc: number, serv: any) =>
      acc + parseMoney(serv.maoDeObra),
    0
  );

  const totalPecasCompra = servicesOfMonth.reduce(
    (acc: number, serv: any) => {
      if (serv.extras?.pecaLoja_extra) {
        const pecasValue =
          serv.extras.pecaLoja_extra.reduce(
            (pecaAcc: number, peca: any) =>
              pecaAcc + parseMoney(peca.valorCompra),
            0
          );

        return acc + pecasValue;
      }

      return acc;
    },
    0
  );

  const netProfit =
    totalBilling - totalExpenses - totalPecasCompra;


  const billingData = services.reduce(
    (acc: any[], serv: any) => {
      const d = getServiceDate(serv.dataEntrada);

      const monthIndex = d.getMonth();
      const year = d.getFullYear();

      const month = d.toLocaleString("pt-BR", {
        month: "long",
      });

      const formattedMonth =
        month.charAt(0).toUpperCase() +
        month.slice(1);

      const period = `${formattedMonth} ${year}`;

      const existing = acc.find(
        (item) =>
          item.monthIndex === monthIndex &&
          item.year === year
      );

      if (existing) {
        existing.billing += parseMoney(
          serv.valorConserto
        );
      } else {
        acc.push({
          monthIndex,
          year,
          period,
          billing: parseMoney(
            serv.valorConserto
          ),
        });
      }

      return acc;
    },
    []
  );

  billingData.sort(
    (
      a: { year: number; monthIndex: number },
      b: { year: number; monthIndex: number }
    ) => {
      if (a.year === b.year) {
        return a.monthIndex - b.monthIndex;
      }

      return a.year - b.year;
    }
  );

  const formattedBillingData = billingData.map(
    (item: any) => ({
      period: item.period,
      billing: Number(item.billing.toFixed(2)),
    })
  );

  console.log(
    "Dados do gráfico:",
    formattedBillingData
  );


  const servicesData = servicesOfMonth.map(
    (serv: any) => ({
      tipo: serv.tipoConserto,
      quantidade: 1,
    })
  );

  return (
    <Styled.Container>
      <Styled.Content>
        <MetricCard
          title="Faturamento"
          value={`R$ ${formatMoney(totalBilling)}`}
          icon={DollarSign}
          trend={0}
        />

        <MetricCard
          title="Clientes"
          value={totalClients}
          icon={Users}
          trend={0}
        />

        <MetricCard
          title="Serviços"
          value={totalServices}
          icon={ShoppingCart}
          trend={0}
        />
      </Styled.Content>

      <FinancialSummary
        faturamentoBruto={formatMoney(
          totalBilling
        )}
        despesas={formatMoney(totalExpenses)}
        totalPecasCompra={formatMoney(
          totalPecasCompra
        )}
        lucroLiquido={formatMoney(netProfit)}
      />

      <Styled.ContentGraphs>
        <BillingByPeriod
          data={formattedBillingData}
          onSelectMonth={setSelectedMonth}
        />

        <ServicesbyType data={servicesData} />
      </Styled.ContentGraphs>

      {selectedMonth && (
        <h2 style={{ textAlign: "center" }}>
          Dados de {selectedMonth}
        </h2>
      )}

      {selectedMonth && (
        <div
          style={{
            textAlign: "center",
            margin: "20px",
          }}
        >
          <button
            onClick={() => setSelectedMonth("")}
            style={{
              color: "#fff",
              border: "none",
              padding: "10px",
              fontSize: "16px",
              cursor: "pointer",
              borderRadius: "6px",
              background: "#8884D8",
            }}
          >
            Ver todos os meses
          </button>
        </div>
      )}
    </Styled.Container>
  );
};

export default Home;